import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Crown, Zap, Check, AlertCircle, LogOut } from 'lucide-react';
import SubscriptionPage from './SubscriptionPage';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Mandatory access gate.
 *
 * If the user has an active paid plan or a Stripe trial-with-card, renders the
 * normal children (the full Dashboard).
 *
 * If the user is on the "needs_subscription" state, we REPLACE the entire UI
 * with the subscription picker + a locked banner + a log-out button. There is
 * NO hamburger menu, no navigation, no sidebar. The only exits are:
 *   1. Complete Stripe Checkout (which flips access_state to "trial" or "premium")
 *   2. Log out
 */
const AccessGate = ({ children }) => {
  const [access, setAccess] = useState(null);
  const [checking, setChecking] = useState(true);
  const { logout, token } = useAuth();

  useEffect(() => {
    const check = async () => {
      try {
        const authToken = token || localStorage.getItem('token');
        if (!authToken) { setChecking(false); return; }
        const res = await axios.get(`${API}/subscription/access`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setAccess(res.data);
      } catch (err) {
        console.error('Access check failed:', err);
      } finally {
        setChecking(false);
      }
    };
    check();
    // Re-check every 30s so a successful Stripe checkout auto-unlocks the app
    // without requiring a hard reload.
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [token]);

  if (checking) return children;

  // Premium or active trial → full app access.
  if (!access || ['premium', 'trial'].includes(access.access_state)) {
    return children;
  }

  // needs_subscription → replace UI entirely. No hamburger, no dashboard.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900" data-testid="access-gate-locked">
      {/* Locked banner */}
      <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white px-4 py-3 shadow-md sticky top-0 z-30">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertCircle size={18} className="flex-shrink-0" />
            <span className="text-sm font-semibold">Choose a plan to unlock NutriFitness AI</span>
          </div>
          <button
            onClick={logout}
            data-testid="access-gate-logout"
            className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-full transition-colors"
          >
            <LogOut size={14} />
            Log out
          </button>
        </div>
      </div>

      {/* Trial benefits hero */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6600] to-[#FF8533] flex items-center justify-center flex-shrink-0">
              <Crown size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">Start Your 7-Day Free Trial</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Add a payment method — cancel any time before day 7, no charge.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Perk text="AI meal + workout plans" />
            <Perk text="Unlimited food scans" />
            <Perk text="AI nutrition coaching" />
            <Perk text="3D exercise demos" />
            <Perk text="Cancel anytime" />
          </div>
        </div>

        {/* Inline subscription picker — Stripe Checkout collects card + starts trial */}
        <SubscriptionPage token={token} onUpgrade={() => window.location.reload()} hideFree={true} />
      </div>
    </div>
  );
};

const Perk = ({ text }) => (
  <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200">
    <div className="w-4 h-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
      <Check size={10} className="text-green-600 dark:text-green-400" />
    </div>
    <span>{text}</span>
  </div>
);

// Keep the Zap import so eslint doesn't complain if unused
// eslint-disable-next-line no-unused-vars
const _icons = Zap;

export default AccessGate;
